<template>
    <section class="relative">
      <!-- Background image -->
      <nuxt-img
        class="absolute z-10 object-cover object-top w-full h-full"
        sizes="xs:330px sm:440px md:760px lg:1024px xl:1200px 2xl:1400px"
        loading="lazy"
        format="webp"
        width="1920"
        height="1080"
        :src="backgroundImage"
        :alt="backgroundAlt"
      />
  
      <div class="relative z-10 max-w-screen-xl py-8 mx-auto sm:px-4 lg:py-16 2xl:px-0">
        <div class="grid grid-cols-11 sm:grid-cols-12">
          <!-- Left column border -->
          <div class="col-span-4 border-b-4 sm:col-span-5" :style="{ borderColor: leftBorderColor }">
          </div>
  
          <!-- Middle column with image and text -->
          <div class="col-span-3 bg-white border-b-4 rounded-t-3xl" :style="{ borderColor: middleBorderColor }">
            <nuxt-img
              class="w-11/12 mx-auto -mt-8 md:w-8/12"
              sizes="sm:261px md:261px lg:261px xl:261px"
              loading="lazy"
              format="webp"
              width="1920"
              height="1080"
              :src="middleImage"
              :alt="middleImageAlt"
            />
            <div class="px-1 pb-4 text-center font-ww-medium xl:text-3xl md:text-xl sm-max:leading-5" v-html="middleText"></div>
          </div>
  
          <!-- Right column text -->
          <div
            class="grid items-end col-span-4 px-2 pb-4 text-center text-white border-b-4 sm-max:leading-5 font-ww-medium xl:text-3xl md:text-xl"
            :style="{ borderColor: rightBorderColor }"
            v-html="rightText"
          >
          </div>
        </div>
  
        <!-- Additional rows with similar structure -->
        <div v-for="(row, index) in rows" :key="index" class="grid grid-cols-11 sm:grid-cols-12 item-center">
          <div
            class="grid items-center justify-center col-span-4 px-2 text-center text-white border-b-4 md:text-right sm:col-span-5 md:justify-end md:px-5 font-ww-medium xl:text-2xl md:text-xl"
            :style="{ borderColor: row.leftBorderColor }"
            v-html="row.leftText"
          >
          </div>
  
          <div class="grid items-center col-span-3 bg-white">
            <div class="items-center text-center border-b-4 xl:text-2xl md:text-xl font-ww-medium" :style="{ borderColor: row.middleBorderColor }">
              <nuxt-img class="mx-auto xl:w-24 md:w-20 w-14" loading="lazy" format="webp" width="97" height="91" :src="row.middleImage" :alt="row.middleImageAlt" />
            </div>
          </div>
  
          <div
            class="grid items-center col-span-4 text-center text-white border-b-4"
            :style="{ borderColor: row.rightBorderColor }"
          >
            <nuxt-img class="mx-auto xl:w-24 md:w-20 w-14" loading="lazy" format="webp" width="97" height="91" :src="row.rightImage" :alt="row.rightImageAlt" />
          </div>
        </div>
  
        <!-- Special last row (different structure) -->
        <div class="grid grid-cols-11 sm:grid-cols-12 item-center">
          <div
            class="grid items-center justify-center col-span-4 px-2 text-sm text-center text-white sm-max:pt-2 sm:col-span-5 md:justify-end md:px-5 md:md:text-right sm:text-base xl:text-2xl md:text-xl sm-max:leading-4 xl:-mt-10 font-ww-medium"
            v-html="lastRowLeftText"
          >
          </div>
          <div class="grid items-center col-span-3 pb-4 bg-white xl:pb-10 rounded-b-3xl">
            <div class="items-center text-center xl:text-2xl md:text-xl font-ww-medium">
              <nuxt-img class="mx-auto xl:w-24 md:w-20 w-14" loading="lazy" format="webp" width="97" height="91" :src="lastRowMiddleImage" :alt="lastRowMiddleImageAlt" />
            </div>
          </div>
          <div
            class="grid items-center col-span-4 -mt-4 text-base text-center text-white md:px-10 xl:text-3xl md:xl:text-2xl md:text-xl sm-max:leading-4 xl:-mt-10"
          >
            <nuxt-img class="mx-auto xl:w-24 md:w-20 w-14" loading="lazy" format="webp" width="97" height="91" :src="lastRowRightImage" :alt="lastRowRightImageAlt" />
          </div>
        </div>
      </div>
    </section>
  </template>
  
  <script setup>
  const props = defineProps({
    backgroundImage: {
      type: String,
      default: '/assets/images/COMPARISON_BACKGROUND.png',
    },
    backgroundAlt: {
      type: String,
      default: 'background',
    },
    leftBorderColor: {
      type: String,
      default: '#8ca798',
    },
    middleBorderColor: {
      type: String,
      default: '#FFFFFF',
    },
    rightBorderColor: {
      type: String,
      default: '#8ca798',
    },
    middleImage: {
      type: String,
      default: '/assets/images/mushroom1.png',
    },
    middleImageAlt: {
      type: String,
      default: 'middle image',
    },
    middleText: {
      type: String,
      default: 'LIFEBOOST <br> <strong>COGNITION</strong>',
    },
    rightText: {
      type: String,
      default: 'OTHER MUSHROOM COFFEE BRANDS',
    },
    rows: {
      type: Array,
      default: () => [
        {
          leftText: 'CERTIFIED ORGANIC',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Certified Organic',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-3.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        {
          leftText: 'CHEMICAL-FREE CULTIVATION',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Chemical-Free',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-3.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        {
          leftText: 'EXTRACTED FROM FRUITING BODIES',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Extracted from Fruiting Bodies',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-3.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        {
          leftText: 'LOW-ACID',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Low-Acid',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-4.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
      ],
    },
    lastRowLeftText: {
      type: String,
      default: 'MUSHROOMS EXTRACTS 8X MORE POTENT THAN ANY OTHER ON MARKET',
    },
    lastRowMiddleImage: {
      type: String,
      default: '/assets/images/Untitled-1.png',
    },
    lastRowMiddleImageAlt: {
      type: String,
      default: 'middle image',
    },
    lastRowRightImage: {
      type: String,
      default: '/assets/images/Untitled-4.png',
    },
    lastRowRightImageAlt: {
      type: String,
      default: 'right image',
    },
  });
  
  defineBuilderComponent({
    friendlyName: "ComparisonSectionProduct",
    inputs: [
      {
        name: "backgroundImage",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/COMPARISON_BACKGROUND.png",
      },
      {
        name: "backgroundAlt",
        type: "string",
        defaultValue: "background",
      },
      {
        name: "leftBorderColor",
        type: "color",
        defaultValue: "#8ca798",
      },
      {
        name: "middleBorderColor",
        type: "color",
        defaultValue: "#FFFFFF",
      },
      {
        name: "rightBorderColor",
        type: "color",
        defaultValue: "#8ca798",
      },
      {
        name: "middleImage",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/mushroom1.png",
      },
      {
        name: "middleImageAlt",
        type: "string",
        defaultValue: "middle image",
      },
      {
        name: "middleText",
        type: "richText",
        defaultValue: 'LIFEBOOST <br> <strong>COGNITION</strong>',
      },
      {
        name: "rightText",
        type: "richText",
        defaultValue: 'OTHER MUSHROOM COFFEE BRANDS',
      },
      {
        name: "rows",
        type: "list",
        defaultValue: [
        {
          leftText: 'CERTIFIED ORGANIC',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Certified Organic',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-3.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        {
          leftText: 'CHEMICAL-FREE CULTIVATION',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Chemical-Free',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-3.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        {
          leftText: 'EXTRACTED FROM FRUITING BODIES',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Extracted from Fruiting Bodies',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-3.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        {
          leftText: 'LOW-ACID',
          leftBorderColor: '#8ca798',
          middleImage: '/assets/images/Untitled-1.png',
          middleImageAlt: 'Low-Acid',
          middleBorderColor: '#FFFFFF',
          rightImage: '/assets/images/Untitled-4.png',
          rightImageAlt: 'Other Brand',
          rightBorderColor: '#8ca798',
        },
        ],
        subFields: [
          {
            name: "leftText",
            type: "richText",
            defaultValue: "Left Column Text",
          },
          {
            name: "leftBorderColor",
            type: "color",
            defaultValue: "#8ca798",
          },
          {
            name: "middleImage",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            defaultValue: "/assets/images/Untitled-1.png",
          },
          {
            name: "middleImageAlt",
            type: "string",
            defaultValue: "Middle Image Alt",
          },
          {
            name: "middleBorderColor",
            type: "color",
            defaultValue: "#FFFFFF",
          },
          {
            name: "rightImage",
            type: "file",
            allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
            defaultValue: "/assets/images/Untitled-3.png",
          },
          {
            name: "rightImageAlt",
            type: "string",
            defaultValue: "Right Image Alt",
          },
          {
            name: "rightBorderColor",
            type: "color",
            defaultValue: "#8ca798",
          },
        ],
      },
      {
        name: "lastRowLeftText",
        type: "richText",
        defaultValue: 'MUSHROOMS EXTRACTS 8X MORE POTENT THAN ANY OTHER ON MARKET',
      },
      {
        name: "lastRowMiddleImage",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/Untitled-1.png",
      },
      {
        name: "lastRowMiddleImageAlt",
        type: "string",
        defaultValue: "middle image",
      },
      {
        name: "lastRowRightImage",
        type: "file",
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        defaultValue: "/assets/images/Untitled-4.png",
      },
      {
        name: "lastRowRightImageAlt",
        type: "string",
        defaultValue: "right image",
      },
    ],
  });
  </script>
  